import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
    Brain, Heart, Sparkles, ChevronRight, ArrowRight, 
    Check, Zap, Shield, RefreshCw, Activity, Waves, Clock, Eye
} from 'lucide-react';
import CoRegulateLogo from '../components/Logo';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';

// JSON-LD Structured Data for SEO
const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
        {
            "@type": "WebPage",
            "@id": "https://coregulateadhd.com/adhd-emotional-regulation",
            "url": "https://coregulateadhd.com/adhd-emotional-regulation",
            "name": "ADHD Emotional Regulation Tools | CoRegulate",
            "description": "Practical, step-by-step tools for managing emotional dysregulation, overwhelm, and shutdown — designed specifically for how ADHD brains work.",
            "isPartOf": { "@id": "https://coregulateadhd.com/#website" },
            "about": { "@id": "https://coregulateadhd.com/#organization" },
            "primaryImageOfPage": { "@id": "https://coregulateadhd.com/og-image.png" }
        },
        {
            "@type": "Organization",
            "@id": "https://coregulateadhd.com/#organization",
            "name": "CoRegulate",
            "url": "https://coregulateadhd.com",
            "logo": "https://coregulateadhd.com/logo.png",
            "description": "Made for ADHD hearts, shaped by ADHD minds. Emotional regulation tools designed specifically for ADHD nervous systems."
        },
        {
            "@type": "FAQPage",
            "mainEntity": [
                {
                    "@type": "Question",
                    "name": "What is emotional dysregulation in ADHD?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Emotional dysregulation in ADHD refers to difficulty managing emotional responses. It can include emotions that hit suddenly and intensely, difficulty calming down once activated, shutting down when overwhelmed, and racing thoughts that won't stop. This happens because ADHD affects the brain's emotional regulation center and lower dopamine makes it harder to self-soothe."
                    }
                },
                {
                    "@type": "Question",
                    "name": "How can I regulate my emotions with ADHD?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "ADHD emotional regulation can be improved with specific tools designed for how ADHD brains work. These include breathing patterns (Box Breathing, 4-7-8, Physiological Sigh), quick sensory grounding exercises (Micro Reset), calming visual meditations, cognitive reframes for negative self-talk, and body awareness practices like body scans."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Why is emotional regulation harder with ADHD?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "ADHD affects the brain's prefrontal cortex, which is responsible for emotional regulation. Lower dopamine levels make it harder to self-soothe, executive function challenges affect emotional responses, and rejection sensitivity (RSD) can amplify emotional pain. These are neurological differences, not character flaws."
                    }
                }
            ]
        },
        {
            "@type": "ItemList",
            "name": "ADHD Emotional Regulation Tools",
            "itemListElement": [
                {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Regulation Navigator",
                    "description": "Guided assessment that recommends the right tool based on your current state"
                },
                {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Breathing Patterns",
                    "description": "5 evidence-based patterns including Box Breathing, 4-7-8, and Physiological Sigh"
                },
                {
                    "@type": "ListItem",
                    "position": 3,
                    "name": "Micro Reset",
                    "description": "Quick 30-second sensory grounding when you need immediate relief"
                },
                {
                    "@type": "ListItem",
                    "position": 4,
                    "name": "EMDR/Bilateral Regulation",
                    "description": "Calm racing thoughts and settle your nervous system with bilateral stimulation"
                },
                {
                    "@type": "ListItem",
                    "position": 5,
                    "name": "Still Sphere",
                    "description": "Calming visual meditation for shutdown and overwhelm states"
                },
                {
                    "@type": "ListItem",
                    "position": 6,
                    "name": "Self-Talk Reframes",
                    "description": "Gentle cognitive reframes for common ADHD negative self-talk patterns"
                },
                {
                    "@type": "ListItem",
                    "position": 7,
                    "name": "Body Scan",
                    "description": "Guided body awareness meditation to reconnect with physical sensations"
                }
            ]
        }
    ]
};

const SEOLandingEmotionalRegulation = () => {
    const navigate = useNavigate();
    
    const tools = [
        {
            name: 'Regulation Navigator',
            description: 'Guided assessment that recommends the right tool based on your current state',
            icon: Brain,
            time: '2-3 min'
        },
        {
            name: 'Breathing Patterns',
            description: '5 evidence-based patterns including Box Breathing, 4-7-8, and Physiological Sigh',
            icon: Waves,
            time: '30 sec - 5 min'
        },
        {
            name: 'Micro Reset',
            description: 'Quick 30-second sensory grounding when you need immediate relief',
            icon: Zap,
            time: '30 sec'
        },
        {
            name: 'EMDR/Bilateral Regulation',
            description: 'Calm racing thoughts with gentle bilateral stimulation',
            icon: Eye,
            time: '1-4 min'
        },
        {
            name: 'Still Sphere',
            description: 'Calming visual meditation for shutdown and overwhelm states',
            icon: Activity,
            time: '1-5 min'
        },
        {
            name: 'Self-Talk Reframes',
            description: 'Gentle cognitive reframes for common ADHD negative self-talk patterns',
            icon: RefreshCw,
            time: '2-3 min'
        },
        {
            name: 'Body Scan',
            description: 'Guided body awareness meditation to reconnect with physical sensations',
            icon: Shield,
            time: '5-10 min'
        }
    ];
    
    const benefits = [
        'Designed specifically for ADHD nervous systems',
        'Low-overwhelm, one action per screen',
        'No walls of text or complicated instructions',
        'Sensory-friendly colors and gentle animations',
        'Trauma-informed language throughout',
        'AI Guide that helps you find the right tool'
    ];

    return (
        <div className="min-h-screen bg-white/60">
            {/* SEO Meta Tags and Structured Data */}
            <Helmet>
                <title>ADHD Emotional Regulation Tools | CoRegulate</title>
                <meta name="description" content="Practical, step-by-step tools for managing emotional dysregulation, overwhelm, and shutdown — designed specifically for how ADHD brains work." />
                <meta name="keywords" content="ADHD emotional regulation, ADHD emotional dysregulation, ADHD tools, emotional regulation techniques, ADHD self-regulation" />
                <link rel="canonical" href="https://coregulateadhd.com/adhd-emotional-regulation" />
                <meta property="og:title" content="ADHD Emotional Regulation Tools | CoRegulate" />
                <meta property="og:description" content="Practical, step-by-step tools for managing emotional dysregulation, overwhelm, and shutdown — designed specifically for how ADHD brains work." />
                <meta property="og:url" content="https://coregulateadhd.com/adhd-emotional-regulation" />
                <meta property="og:type" content="website" />
            </Helmet>
            
            {/* JSON-LD Structured Data */}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
            
            {/* Header */}
            <header className="sticky top-0 z-50 bg-white/60/95 backdrop-blur-sm border-b border-white/40/30">
                <nav className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
                    <Link to="/landing" className="flex items-center gap-2">
                        <CoRegulateLogo size="md" />
                    </Link>
                    <div className="flex items-center gap-3">
                        <Link 
                            to="/auth?mode=login"
                            className="hidden sm:flex px-4 py-2 text-sm text-[#7D8E95] hover:text-[#1D6B6B] transition-colors"
                        >
                            Sign in
                        </Link>
                        <Link 
                            to="/initial-assessment"
                            className="px-5 py-2.5 bg-gradient-to-r from-[#1D6B6B] to-[#145555] text-white text-sm font-medium rounded-full hover:shadow-lg transition-all"
                            data-testid="nav-cta"
                        >
                            Try Free
                        </Link>
                    </div>
                </nav>
            </header>
            
            {/* Hero Section */}
            <section className="py-16 md:py-24 overflow-hidden">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1D6B6B]/10 rounded-full mb-6">
                        <Brain className="w-4 h-4 text-[#1D6B6B]" />
                        <SmallText className="text-[#1D6B6B] font-medium">Made for ADHD</SmallText>
                    </div>
                    
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-[#1D6B6B] mb-6" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                        ADHD Emotional Regulation Tools
                    </h1>
                    
                    <p className="text-lg md:text-xl text-[#7D8E95] max-w-2xl mx-auto mb-8">
                        Practical, step-by-step tools for managing emotional dysregulation, overwhelm, and shutdown — designed specifically for how ADHD brains work.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button 
                            onClick={() => navigate('/initial-assessment')}
                            className="group px-8 py-4 bg-gradient-to-r from-[#1D6B6B] to-[#145555] text-white font-medium rounded-full hover:shadow-lg transition-all flex items-center justify-center gap-2"
                            data-testid="hero-cta"
                        >
                            Start Regulating
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                        <button 
                            onClick={() => navigate('/blog')}
                            className="px-8 py-4 bg-white/70 text-[#1D6B6B] font-medium rounded-full border border-[#7D8E95]/30 hover:bg-white transition-all"
                        >
                            Learn About ADHD
                        </button>
                    </div>
                </div>
            </section>
            
            {/* What is Emotional Dysregulation */}
            <section className="py-16 bg-white/70">
                <div className="max-w-4xl mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-2xl md:text-3xl font-semibold text-[#1D6B6B] mb-4" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                            Understanding ADHD Emotional Dysregulation
                        </h2>
                        <p className="text-[#7D8E95] max-w-2xl mx-auto">
                            Emotional dysregulation isn't a character flaw — it's how ADHD brains are wired.
                        </p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-white/60 rounded-2xl p-6 border border-white/40/20">
                            <h3 className="font-medium text-[#1D6B6B] mb-3">What it feels like</h3>
                            <ul className="space-y-2 text-[#7D8E95]">
                                <li className="flex items-start gap-2">
                                    <span className="text-[#FFBB98] mt-1">•</span>
                                    Emotions that hit suddenly and intensely
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#FFBB98] mt-1">•</span>
                                    Difficulty calming down once activated
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#FFBB98] mt-1">•</span>
                                    Shutting down when overwhelmed
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#FFBB98] mt-1">•</span>
                                    Racing thoughts that won't stop
                                </li>
                            </ul>
                        </div>
                        
                        <div className="bg-white/60 rounded-2xl p-6 border border-white/40/20">
                            <h3 className="font-medium text-[#1D6B6B] mb-3">Why it happens</h3>
                            <ul className="space-y-2 text-[#7D8E95]">
                                <li className="flex items-start gap-2">
                                    <span className="text-[#1D6B6B] mt-1">•</span>
                                    ADHD affects the brain's emotional regulation center
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#1D6B6B] mt-1">•</span>
                                    Lower dopamine makes it harder to self-soothe
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#1D6B6B] mt-1">•</span>
                                    Executive function challenges affect emotional response
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#1D6B6B] mt-1">•</span>
                                    Rejection sensitivity amplifies emotional pain
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>
            
            {/* Tools Grid */}
            <section className="py-16">
                <div className="max-w-5xl mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-2xl md:text-3xl font-semibold text-[#1D6B6B] mb-4" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                            Regulation Tools That Actually Work
                        </h2>
                        <p className="text-[#7D8E95]">
                            Evidence-based techniques adapted for ADHD nervous systems
                        </p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {tools.map((tool, index) => (
                            <div 
                                key={tool.name}
                                className="bg-white/60 rounded-2xl p-6 border border-white/40/20 hover:shadow-lg hover:-translate-y-1 transition-all"
                            >
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1D6B6B]/10 to-[#1D6B6B]/20 flex items-center justify-center mb-4">
                                    <tool.icon className="w-6 h-6 text-[#1D6B6B]" />
                                </div>
                                <h3 className="font-medium text-[#1D6B6B] mb-2">{tool.name}</h3>
                                <p className="text-sm text-[#7D8E95] mb-3">{tool.description}</p>
                                <div className="flex items-center gap-2 text-xs text-[#7D8E95]">
                                    <Clock className="w-3.5 h-3.5" />
                                    {tool.time}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            
            {/* Benefits */}
            <section className="py-16 bg-white/70">
                <div className="max-w-4xl mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-2xl md:text-3xl font-semibold text-[#1D6B6B] mb-4" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                            Why CoRegulate Works for ADHD
                        </h2>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                        {benefits.map((benefit, index) => (
                            <div 
                                key={index}
                                className="flex items-start gap-3 bg-white/60 rounded-xl p-4 border border-white/40/20"
                            >
                                <div className="w-6 h-6 rounded-full bg-[#1D6B6B]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <Check className="w-4 h-4 text-[#1D6B6B]" />
                                </div>
                                <span className="text-[#3A3A37]">{benefit}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            
            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-br from-[#1D6B6B] to-[#145555]">
                <div className="max-w-3xl mx-auto px-4 text-center">
                    <Heart className="w-12 h-12 text-white/80 mx-auto mb-6" />
                    <h2 className="text-3xl font-semibold text-white mb-4" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                        Start Your Regulation Journey
                    </h2>
                    <p className="text-white/80 mb-8 max-w-xl mx-auto">
                        Take a gentle assessment to discover which tools match your needs. 
                        No pressure, no judgment — just practical support for your ADHD brain.
                    </p>
                    <button 
                        onClick={() => navigate('/initial-assessment')}
                        className="group px-10 py-4 bg-white text-[#1D6B6B] font-semibold rounded-full hover:bg-white/60 hover:shadow-lg transition-all flex items-center justify-center gap-2 mx-auto"
                        data-testid="final-cta"
                    >
                        Take the Free Assessment
                        <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                </div>
            </section>
            
            {/* Footer */}
            <footer className="bg-[#145555] text-white py-12">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <CoRegulateLogo size="small" variant="light" />
                    <p className="text-white/60 text-sm mt-4 mb-6">
                        Made for ADHD hearts, shaped by ADHD minds.
                    </p>
                    <div className="flex justify-center gap-6 text-sm text-white/60">
                        <Link to="/landing" className="hover:text-white transition-colors">Home</Link>
                        <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
                        <Link to="/adhd-relationship-tools" className="hover:text-white transition-colors">Relationship Tools</Link>
                        <Link to="/crisis" className="hover:text-white transition-colors">Crisis Resources</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default SEOLandingEmotionalRegulation;
